import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import { motion, AnimatePresence } from 'framer-motion';
import Moment from 'react-moment';
import StandardVideo from '../media/standardVideo';

import { Arrow } from '@svg/arrow';
import Close from '@svg/close.svg';
import { useScrollFreeze } from '@hooks';

const playerVars = {
  open: {
    opacity: 1,
    transition: {
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  },
  closed: {
    opacity: 0,
    transition: {
      duration: 0.5,
      ease: [0.43, 0.13, 0.23, 0.96],
    },
  },
};

export const EventWidget = ({ setPopupOpen, ...props }) => {
  const {
    title,
    session,
    where,
    slug,
    ticketsLink,
    series,
    isOneOff,
    freeEvent,
    hideTicketingInfo,
  } = props.event || {};

  return (
    <>
      <div className='mt-8 lg:mt-16'>
        <div className='border-b border-grey w-full'>
          <p>{series}</p>
          <h3 className='font-medium !mt-0'>{title}</h3>
        </div>

        <div className='border-b border-grey w-full grid grid-cols-10'>
          <p className='col-span-3 lg:col-span-2'>When:</p>

          <div className='col-start-5 lg:col-start-4 col-span-6'>
            {session &&
              session.map((date, i) => {
                return (
                  <p key={i}>
                    <Moment withTitle format='ddd DD MMM YYYY - h:mm A'>
                      {date}
                    </Moment>
                  </p>
                );
              })}
          </div>
        </div>

        <div className='border-b border-grey w-full grid grid-cols-10'>
          <p className='col-span-3 lg:col-span-2'>Where:</p>
          <p className='col-start-5 lg:col-start-4 col-span-5'>
            {where && where.title}
          </p>
        </div>

        {ticketsLink && (
          <div className='border-b border-grey w-full grid grid-cols-10 items-center'>
            <p className='col-span-3 lg:col-span-2'>Concert info:</p>
            <Link
              to={`/events/${slug && slug?.current}`}
              className='underline-important font-semibold col-start-5 lg:col-start-4 col-span-5'>
              Read more
            </Link>
          </div>
        )}

        {ticketsLink && !hideTicketingInfo && (
          <div className='border-b border-grey w-full grid grid-cols-10 items-center'>
            <p className='col-span-3 lg:col-span-2'>Ticketing:</p>
            <p
              onClick={() => setPopupOpen(true)}
              className='underline-important font-semibold col-start-5 lg:col-start-4 col-span-7 cursor-pointer'>
              Single Concert vs Series Packages
            </p>
          </div>
        )}
        {freeEvent && !hideTicketingInfo && (
          <div className='border-t border-grey w-full grid grid-cols-10 items-center'>
            <p className='col-span-3 lg:col-span-2'>Ticketing:</p>
            <p className='underline-important font-semibold col-start-5 lg:col-start-4 col-span-7 '>
              Free Admission
            </p>
          </div>
        )}

        <div className='mt-8 lg:mt-14 flex justify-around sm:justify-start xl:block'>
          <div className='inline'>
            {ticketsLink ? (
              <a
                href={ticketsLink}
                target='_blank'
                rel='noreferrer'
                className='btn-color-white btn-white'>
                Book Tickets
              </a>
            ) : (
              <Link
                to={`/events/${slug && slug?.current}`}
                className='btn-white'>
                Concert Info
              </Link>
            )}
          </div>
          {!isOneOff && (
            <div className='inline sm:ml-4 '>
              <Link to='/season-subscription/select' className='btn-black'>
                Subscribe Now
              </Link>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const FormWidget = ({ ...props }) => {
  const [hover, setHover] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    window.innerWidth < 1024 && setIsMobile(true);
  }, []);

  let arrowVars = {};

  if (!isMobile) {
    arrowVars = {
      initial: {
        x: -20,
        transition: {
          duration: 0.3,
        },
      },
      animate: {
        x: 0,
        transition: {
          duration: 0.3,
        },
      },
    };
  }

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    let myForm = document.getElementById('enquiry-form');
    let formData = new FormData(myForm);
    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams(formData).toString(),
    })
      .then(() => console.log('Form successfully submitted'))
      .catch((error) => alert(error));
  };

  const { form } = props || {};

  // The "fields" fields are being pulled through with a capital F
  const fields = form?.fields ?? form?.['Fields'];

  return (
    <div>
      <AnimatePresence>
        {submitted ? (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className='text-base leading-base md:text-xl md:leading-xl'>
            {props.form.confirmationText}
          </motion.span>
        ) : (
          <motion.form
            id='enquiry-form'
            netlify-honeypot='bot-field'
            name={form.mailTo}
            method='POST'
            data-netlify='true'
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onSubmit={(e) => handleSubmit(e)}
            className={`flex flex-col ${
              props.whiteText ? 'ref-form-white' : 'ref-form-black'
            } ref-form`}>
            <input type='hidden' name='form-name' value={form.mailTo} />
            {fields?.map((row, i) => {
              return (
                <input
                  key={i}
                  className='text-sm leading-sm md:text-xl md:leading-xl pb-2 pt-5 lg:pt-8 focus:outline-none border-b border-grey'
                  placeholder={row.input}
                  name={row.input}
                  required={row.required}
                  type='text'
                />
              );
            })}

            {form?.selects?.map((input, i) => {
              return (
                <div
                  key={i}
                  className='relative w-full flex items-center justify-center'>
                  <select
                    style={{ background: 'rgba(0, 0, 0, 0)' }}
                    className='w-full text-sm leading-sm md:text-xl md:leading-xl pb-2 pt-5 lg:pt-8 focus:outline-none border-b border-grey'
                    name={input.select}>
                    <option selected disabled hidden>
                      {input.select}
                    </option>
                    {input.options.map((label, i) => (
                      <option key={i} value={label}>
                        {label}
                      </option>
                    ))}
                  </select>

                  <div className='absolute right-0 bottom-5 pointer-events-none'>
                    <svg
                      className='w-2 lg:w-4'
                      viewBox='0 0 18 10'
                      fill={props.whiteText ? 'white' : 'black'}
                      xmlns='http://www.w3.org/2000/svg'>
                      <path
                        d='M9 10L0.339745 0.25L17.6603 0.250002L9 10Z'
                        fill={props.whiteText ? 'white' : 'black'}
                      />
                    </svg>
                  </div>
                </div>
              );
            })}

            <textarea
              className='text-sm leading-sm md:text-xl md:leading-xl pb-2 pt-5 lg:pt-8 focus:outline-none border-b border-grey'
              placeholder='Your Message'
              rows='4'
              name='Message'
            />

            <motion.button
              initial='initial'
              whileHover='animate'
              onMouseEnter={() => setHover(true)}
              onMouseLeave={() => setHover(false)}
              type='submit'
              className='flex flex-row text-sm leading-sm md:text-lg md:leading-lg text-black justify-between items-center mt-6 lg:mt-12 font-semibold'>
              <span className={props.whiteText ? 'text-white' : 'text-black'}>
                Submit
              </span>{' '}
              <motion.span variants={arrowVars}>
                <Arrow white={props.whiteText ? true : false} />
              </motion.span>
            </motion.button>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};

const PlayerModal = ({ setPlayerOpen, url }) => {
  useScrollFreeze();

  return (
    <motion.div
      variants={playerVars}
      initial='closed'
      animate='open'
      exit='closed'
      className='fixed h-full w-screen top-0 left-0 bg-black bg-opacity-50 z-50 flex items-center justify-center'>
      <button
        onClick={() => setPlayerOpen(false)}
        className='absolute top-0 lg:top-5 right-gutter lg:right-halfGutter focus:outline-none z-50'>
        <Close className='w-10 lg:w-16' />
      </button>

      <div className='w-full h-full absolute flex items-center px-gutter z-30 react-player'>
        <StandardVideo url={url} playing width='100%' height='auto' />
      </div>
    </motion.div>
  );
};

export const Player = ({ playerOpen, setPlayerOpen, url }) => {
  return (
    <AnimatePresence exitBeforeEnter>
      {playerOpen && (
        <PlayerModal key={playerOpen} url={url} setPlayerOpen={setPlayerOpen} />
      )}
    </AnimatePresence>
  );
};
